import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Loader2 } from "lucide-react";
import { aiKefuService, type Servicer } from "../../api/aiKefuService";

type Props = {
  open: boolean;
  sessionId: string | null;
  onClose: () => void;
  onConfirm: (servicerUserid: string) => void;
  submitting?: boolean;
};

const STATUS_LABEL_KEYS: Record<number, string> = {
  0: "takeover.statusOnline",
  1: "takeover.statusBusy",
  2: "takeover.statusOffline",
  3: "takeover.statusDnd",
};

/** 排序权重：在线 → 忙 → 请勿打扰 → 离线 → 状态未知；让运营优先看到可用坐席。 */
const STATUS_PRIORITY: Record<number, number> = {
  0: 0,
  1: 1,
  3: 2,
  2: 3,
};

function statusWeight(status: number | null | undefined): number {
  if (status == null) return 4;
  return STATUS_PRIORITY[status] ?? 4;
}

function sortServicers(list: Servicer[]): Servicer[] {
  return [...list].sort((a, b) => {
    const diff = statusWeight(a.status) - statusWeight(b.status);
    if (diff !== 0) return diff;
    return a.userid.localeCompare(b.userid);
  });
}

export function TakeoverDialog({ open, sessionId, onClose, onConfirm, submitting }: Props) {
  const { t } = useTranslation("customer-service");
  const [selected, setSelected] = useState<string | null>(null);

  const { data: servicersRaw = [], isLoading } = useQuery({
    queryKey: ["cs-servicers", sessionId],
    queryFn: () => aiKefuService.listServicers(sessionId!),
    enabled: open && !!sessionId,
    staleTime: 0,
  });
  const servicers = useMemo(() => sortServicers(servicersRaw), [servicersRaw]);

  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("takeover.title")}</DialogTitle>
          <DialogDescription>{t("takeover.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1 max-h-[50vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t("takeover.loading")}
            </div>
          ) : servicers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {t("takeover.emptyServicers")}
            </p>
          ) : (
            servicers.map((s) => {
              const isOffline = s.status === 2;
              const active = selected === s.userid;
              return (
                // label 已通过 htmlFor 关联 radio(id 同步设置)并内嵌可见文本;
                // 规则的文本探测深度无法识别多层嵌套的 {s.userid},故 disable。
                // eslint-disable-next-line jsx-a11y/label-has-associated-control
                <label
                  key={s.userid}
                  htmlFor={`takeover-servicer-${s.userid}`}
                  className={`flex items-center gap-3 p-3 rounded-md border
                    ${active ? "border-primary bg-primary/5" : ""}
                    ${
                      isOffline
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer hover:bg-accent/50"
                    }`}
                  title={isOffline ? t("takeover.offlineTooltip") : undefined}
                >
                  <input
                    id={`takeover-servicer-${s.userid}`}
                    type="radio"
                    name="servicer"
                    checked={active}
                    onChange={() => !isOffline && setSelected(s.userid)}
                    disabled={isOffline}
                    className="accent-primary"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{s.userid}</div>
                    <div className="text-2xs text-muted-foreground">
                      {s.status != null
                        ? STATUS_LABEL_KEYS[s.status]
                          ? t(STATUS_LABEL_KEYS[s.status])
                          : t("takeover.statusFallback", { status: s.status })
                        : t("takeover.statusUnknown")}
                      {isOffline ? t("takeover.notDispatchable") : ""}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t("takeover.cancel")}
          </Button>
          <Button
            onClick={() => selected && onConfirm(selected)}
            disabled={!selected || submitting}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t("takeover.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
